import { z } from 'zod';

import {
  MCP_CONNECTION_OPTIONS,
  MCP_RECOMMENDED_OPTIONS,
  mcpConnectionScopes,
  type McpConnectionOption,
} from './mcp-connection';

/**
 * One-click MCP connections (claude.ai, ChatGPT and other remote-MCP clients)
 * authorize over OAuth 2.1. OAuth scopes are coarse, human-readable names that
 * map onto exactly the same PAT scope sets the manual "Connect your own AI
 * agent" form issues (`mcpConnectionScopes`), so both paths enforce identical
 * rights: token scopes ∩ the user's current role, rechecked on every call.
 *
 * `workspace:read` is always granted: every tool needs it.
 */
export const OAUTH_READ_SCOPE = 'workspace:read' as const;

/** Manual-form option id → OAuth scope string. */
export const OAUTH_OPTION_SCOPE: Readonly<Record<McpConnectionOption, string>> = {
  create: 'tasks:create',
  edit: 'tasks:edit',
  status: 'tasks:status',
  assign: 'tasks:assign',
  comment: 'tasks:comment',
  time: 'time:log',
  docs: 'docs:write',
  chat: 'chat:post',
  projects: 'projects:create',
  project: 'projects:settings',
  team: 'teams:settings',
};

export const OAUTH_SCOPE_CATALOG: ReadonlyArray<{
  scope: string;
  option: McpConnectionOption | null;
  label: string;
}> = [
  {
    scope: OAUTH_READ_SCOPE,
    option: null,
    label: 'Read your teams, projects, tasks, comments, docs and your own plan',
  },
  ...MCP_CONNECTION_OPTIONS.map((option) => ({
    scope: OAUTH_OPTION_SCOPE[option.id],
    option: option.id,
    label: option.label,
  })),
];

/**
 * Scopes requested when a client sends none: everyday work (read, tasks,
 * comments, time, docs). The user can still untick any of them on consent.
 */
export const OAUTH_DEFAULT_SCOPES: readonly string[] = [
  OAUTH_READ_SCOPE,
  ...MCP_RECOMMENDED_OPTIONS.map((option) => OAUTH_OPTION_SCOPE[option]),
];

/** Consent pre-ticks these; the others are shown unticked (opt-in). */
export function isRecommendedOAuthScope(scope: string): boolean {
  return OAUTH_DEFAULT_SCOPES.includes(scope);
}

/** Accepted and ignored: we always issue a refresh token. Claude appends it when advertised. */
export const OAUTH_OFFLINE_ACCESS = 'offline_access' as const;

export function isOAuthScope(scope: string): boolean {
  return OAUTH_SCOPE_CATALOG.some((entry) => entry.scope === scope);
}

/** Normalised, de-duplicated grant scopes; always contains `workspace:read`. */
export function normalizeOAuthScopes(scopes: readonly string[]): string[] {
  const wanted = new Set(scopes);
  wanted.add(OAUTH_READ_SCOPE);
  return OAUTH_SCOPE_CATALOG.filter((entry) => wanted.has(entry.scope)).map((entry) => entry.scope);
}

/** OAuth grant scopes → the PAT scope set an access token carries. */
export function oauthPatScopes(scopes: readonly string[]): string[] {
  const options = OAUTH_SCOPE_CATALOG.filter(
    (entry) => entry.option !== null && scopes.includes(entry.scope),
  ).map((entry) => entry.option as McpConnectionOption);
  return mcpConnectionScopes(options);
}

/** GET /api/oauth/consent/:requestId — what the consent page renders. */
export const OAuthConsentViewSchema = z.object({
  requestId: z.string(),
  clientName: z.string(),
  clientUri: z.string().nullable(),
  redirectUri: z.string(),
  redirectHost: z.string(),
  /** True when every redirect URI of the client is a loopback address (native app). */
  loopbackOnly: z.boolean(),
  scopes: z.array(
    z.object({
      scope: z.string(),
      label: z.string(),
      required: z.boolean(),
      /** Pre-ticked on the consent page (everyday work); others are opt-in. */
      recommended: z.boolean(),
    }),
  ),
  /**
   * The browser session is older than the recent-sign-in window: approval would
   * 403 `recent_sign_in_required`. The page asks the user to sign in again first.
   */
  reauthRequired: z.boolean(),
  /** Two-factor is off: the page recommends turning it on (not required). */
  twoFactorEnabled: z.boolean(),
  expiresAt: z.string(),
});
export type OAuthConsentView = z.infer<typeof OAuthConsentViewSchema>;

/** POST /api/oauth/consent/:requestId body. */
export const OAuthConsentDecisionSchema = z
  .object({
    approve: z.boolean(),
    /** Subset of the requested scopes the user kept ticked. Ignored on deny. */
    scopes: z.array(z.string().max(64)).max(32).default([]),
  })
  .strict();
export type OAuthConsentDecision = z.infer<typeof OAuthConsentDecisionSchema>;

export const OAuthConsentResultSchema = z.object({ redirectTo: z.string() });
export type OAuthConsentResult = z.infer<typeof OAuthConsentResultSchema>;

/** GET /api/me/connected-apps — one row per active OAuth grant. */
export const ConnectedAppSchema = z.object({
  id: z.string().uuid(),
  clientName: z.string(),
  clientUri: z.string().nullable(),
  redirectHost: z.string(),
  scopes: z.array(z.string()),
  scopeLabels: z.array(z.string()),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type ConnectedApp = z.infer<typeof ConnectedAppSchema>;
export const ConnectedAppListSchema = z.array(ConnectedAppSchema);
